import "../account.css";
import { Signup } from "../../../ui-components";
import { useState } from "react";
import { useEffect, useCallback, useMemo } from "react";
import { useSelector, useDispatch } from "react-redux";
import { signUp as signUpUser } from "../../../actions/reducers/reducers";
import Verification from "./Verification";
import Success from "./Success";
import { useLocation } from "react-router-dom";
import { useNavigate } from "react-router-dom";
import { resetState } from "../../../actions/action";
import Loader from "../../../utils/loader/Loader";
import GetDiscovered from "../GetDiscovered/GetDiscovered";
import { PrivacyPolicyTermsUseCombined, Close } from "../../../ui-components";
import { Button, Flex, Text, CheckboxField } from "@aws-amplify/ui-react";
import useMediaQuery from "../../../utils/matchMedia/matchMedia";

// TODO make tooltip component from figma
// https://www.figma.com/design/EXQykbSH6t2oA8tfvUPZeB/Ikimy-%3E-AWS-Amplify-UI-Kit-%3E-Candidate?node-id=3846-8234&p=f&m=dev
// create another show tooltip state variable for when the user focuses on the password field
// instead of hovering over the info icon

const selectSignUpLoading = (state) => {
  return state.account.signUpLoading;
};
const selectSignUpError = (state) => {
  return state.account.signUpError;
};
const selectSignUpStatus = (state) => {
  return state.account.signUpStatus;
};

function SignUp() {
  const signUpLoading = useSelector((state) => selectSignUpLoading(state));
  const signUpError = useSelector((state) => selectSignUpError(state));
  const signUpStatus = useSelector((state) => selectSignUpStatus(state));

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [referralCode, setReferralCode] = useState("");
  const [phone, setPhone] = useState("");
  const [dialCode, setDialCode] = useState("+1");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [agreeTermsAndPolicy, setAgreeTermsAndPolicy] = useState(false);

  const [firstNameError, setFirstNameError] = useState("");
  const [lastNameError, setLastNameError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [referralCodeError, setReferralCodeError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");
  const [agreeTermsAndPolicyError, setAgreeTermsAndPolicyError] = useState("");

  const [showTooltip, setShowTooltip] = useState(false);
  const [tosAndPrivatePolicyVisible, setTosAndPrivatePolicyVisible] =
    useState(false);

  const location = useLocation();

  useEffect(() => {
    if (location.state?.email) {
      setEmail(location.state.email);
    }
  }, [location]);

  const formatPhoneNumber = (input) => {
    // Remove all non-digit charactersf
    const digits = input.replace(/\D/g, "");

    // Format as ###-###-####
    let formatted = "";
    if (digits.length > 0) {
      formatted = digits.substring(0, 3);
    }
    if (digits.length > 3) {
      formatted += "-" + digits.substring(3, 6);
    }
    if (digits.length > 6) {
      formatted += "-" + digits.substring(6, 10);
    }

    return formatted;
  };

  const handleFirstNameChange = (event) => {
    setFirstName(event.target.value);
  };
  const handleLastNameChange = (event) => {
    setLastName(event.target.value);
  };
  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };
  const handleReferralCodeChange = (event) => {
    setReferralCode(event.target.value);
  };
  const handlePhoneChange = useCallback((event) => {
    const formatted = formatPhoneNumber(event.target.value);
    setPhone(formatted);
  }, []);
  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };
  const handleConfirmPasswordChange = (event) => {
    setConfirmPassword(event.target.value);
  };
  const handleAgreeTermsAndPolicy = (event) => {
    setAgreeTermsAndPolicy(event.target.checked);
  };
  const handleDialCodeChange = (event) => {
    setDialCode(event.target.value);
  };

  const validateFirstName = useCallback(() => {
    if (firstName.trim() === "") {
      setFirstNameError("First name is required.");
      return false;
    }
    setFirstNameError("");
    return true;
  }, [firstName]);

  const validateLastName = useCallback(() => {
    if (lastName.trim() === "") {
      setLastNameError("Last name is required.");
      return false;
    }
    setLastNameError("");
    return true;
  }, [lastName]);

  const validateEmail = useCallback(() => {
    if (email.trim() === "") {
      setEmailError("Email address is required.");
      return false;
    }
    setEmailError("");
    return true;
  }, [email]);

  const validateReferralCode = useCallback(() => {
    const VALID_REFERRAL_CODE_REGEX = /^[A-Za-z0-9]+$/;
    if (
      referralCode.length > 0 &&
      !VALID_REFERRAL_CODE_REGEX.test(referralCode)
    ) {
      setReferralCodeError("Invalid referral code.");
      return false;
    }
    setReferralCodeError("");
    return true;
  }, [referralCode]);

  useEffect(() => {
    if (signUpError) {
      console.log(signUpError);
      if (
        signUpError.status === 409 &&
        signUpError.message === `User with this email ${email} already exists.`
      ) {
        setEmailError("User with this email address already exists.");
        //return false;
      }
    }
  }, [signUpError, email]);

  const validatePhone = useCallback(() => {
    const VALID_PHONE_NUMBER_REGEX = /^\+\d{1,3}\d{10}$/;
    const phoneForAPI = phone.replaceAll("-", "");

    if (phoneForAPI.trim() === "") {
      setPhoneError("Phone number is required.");
      return false;
    } else if (!VALID_PHONE_NUMBER_REGEX.test(dialCode + phoneForAPI)) {
      setPhoneError("Invalid phone number (do not put spaces or dashes).");
      return false;
    }

    setPhoneError("");
    return true;
  }, [phone, dialCode]);

  const validatePassword = useCallback(() => {
    if (password === "") {
      setPasswordError("Password is required.");
      return false;
    }
    if (password.trim() !== password) {
      setPasswordError("Password cannot begin or end with a space");
      return false;
    }
    if (password.length < 10) {
      setPasswordError("Password must be at least 10 characters long");
      return false;
    }
    if (!/[0-9]/.test(password)) {
      setPasswordError("Password must include at least one number");
      return false;
    }

    if (!/[A-Z]/.test(password)) {
      setPasswordError("Password must include at least one uppercase letter");
      return false;
    }
    if (!/[a-z]/.test(password)) {
      setPasswordError("Password must include at least one lowercase letter");
      return false;
    }

    const VALID_PASSWORD_REGEX =
      /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[\^\$\*\.\[\]\{\}\(\)\?\"\!@#%&\/\\,><\':;|_~\`+= -])[A-Za-z\d\^\$\*\.\[\]\{\}\(\)\?\"\!@#%&\/\\,><\':;|_~\`+= -]{10,}$/;
    if (!VALID_PASSWORD_REGEX.test(password)) {
      setPasswordError("Password must include at least one special symbol");
      return false;
    }

    setPasswordError("");
    return true;
  }, [password]);

  const validateConfirmPassword = useCallback(() => {
    if (confirmPassword === "") {
      setConfirmPasswordError("Please confirm your password.");
      return false;
    }
    if (password !== confirmPassword) {
      setConfirmPasswordError("Passwords do not match.");
      return false;
    }
    setConfirmPasswordError("");
    return true;
  }, [confirmPassword, password]);

  const validateAgreeTermsAndPolicy = useCallback(() => {
    if (!agreeTermsAndPolicy) {
      setAgreeTermsAndPolicyError(
        "Please agree with Terms of service and Privacy Policy.",
      );
      return false;
    }
    setAgreeTermsAndPolicyError("");
    return true;
  }, [agreeTermsAndPolicy]);

  const handleSignUp = useCallback(
    async (event) => {
      // Handle sign-up logic here
      event.preventDefault();

      const validations = [
        validateFirstName,
        validateLastName,
        validateEmail,
        validateReferralCode,
        validatePhone,
        validatePassword,
        validateConfirmPassword,
        validateAgreeTermsAndPolicy,
      ];

      const allValid = validations
        .map((validation) => validation())
        .every((result) => result);

      if (allValid) {
        const signUpRequestBody = {
          email: email,
          referral_code: referralCode,
          password: password,
          confirm_password: confirmPassword,
          given_name: firstName,
          family_name: lastName,
          phone: dialCode + phone.replaceAll("-", ""),
        };
        signUpUser(dispatch, signUpRequestBody);
      }

      // const response = await fetch(
      //     '/signin',
      //     {
      //         method: 'POST',
      //         headers: {
      //             'Content-Type': 'application/json',
      //         },
      //         body: JSON.stringify(data)

      //     }
      // );

      // if (response.ok) {
      //     const data = await response.json();
      //     localStorage.setItem('token', data.AuthenticationResult.AccessToken);
      //     window.location.href = '/dashboard';
      // }
    },
    [
      dispatch,
      firstName,
      lastName,
      email,
      phone,
      dialCode,
      password,
      confirmPassword,
      validateFirstName,
      validateLastName,
      validateEmail,
      validatePassword,
      validateConfirmPassword,
      validatePhone,
      validateAgreeTermsAndPolicy,
      referralCode,
      validateReferralCode,
    ],
  );

  const navigateToSignIn = useCallback(() => {
    dispatch(resetState());
    navigate("/signin");
  }, [navigate, dispatch]);

  const isLargeScreen = useMediaQuery("(min-width: 768px)");

  const signupOverrides = useMemo(() => {
    return {
      Signup: {
        height: "100vh",
        width: isLargeScreen ? "50vw" : "100vw",
        padding: isLargeScreen ? "60px" : "20px",
      },

      AppLogo: {
        display: isLargeScreen ? "flex" : "none",
      },

      "Frame 99": {
        direction: isLargeScreen ? "row" : "column",
      },

      "Frame 102": {
        direction: isLargeScreen ? "row" : "column",
      },

      "Frame 1160464572": {
        direction: isLargeScreen ? "row" : "column",
      },

      "Frame 100": {
        width: "100%",
      },

      "Frame 104": {
        width: "100%",
      },

      TextField38493588: {
        value: firstName,
        onChange: handleFirstNameChange,
        hasError: firstNameError ? true : false,
        errorMessage: firstNameError,
        alignSelf: "stretch",
      },

      TextField38493589: {
        value: lastName,
        onChange: handleLastNameChange,
        hasError: lastNameError ? true : false,
        errorMessage: lastNameError,
        alignSelf: "stretch",
      },

      TextField38493591: {
        value: email,
        onChange: handleEmailChange,
        hasError: emailError ? true : false,
        errorMessage: emailError,
      },

      TextField800126479: {
        value: referralCode,
        onChange: handleReferralCodeChange,
        hasError: referralCodeError ? true : false,
        errorMessage: referralCodeError,
      },

      PhoneNumberField: {
        value: phone,
        onChange: handlePhoneChange,
        placeholder: "Type your phone number (Eg. 6045551234)",
        hasError: phoneError ? true : false,
        errorMessage: phoneError,
        onDialCodeChange: handleDialCodeChange,
        maxLength: "12",
        // onKeyDown: (event) => {
        //     if (event.key === '-' || event.key === 'Backspace') {
        //         const cursorPos = event.target.selectionStart;
        //         if (event.key === '-' && cursorPos === 3) {
        //             event.target.setSelectionRange(4, 4);
        //         } else if (event.key === '-' && cursorPos === 7) {
        //             event.target.setSelectionRange(8, 8);
        //         }
        //     }
        // }
      },

      PasswordField38493595: {
        label: (
          <Flex gap="5px">
            <Text as="span">Password *</Text>
            <div style={{ position: "relative", display: "inline-block" }}>
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{ cursor: "pointer" }}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
              >
                <path
                  d="M7.75 0C12 0 15.5 3.5 15.5 7.75C15.5 12.0312 12 15.5 7.75 15.5C3.46875 15.5 0 12.0312 0 7.75C0 3.5 3.46875 0 7.75 0ZM7.75 3.4375C7 3.4375 6.4375 4.03125 6.4375 4.75C6.4375 5.5 7 6.0625 7.75 6.0625C8.46875 6.0625 9.0625 5.5 9.0625 4.75C9.0625 4.03125 8.46875 3.4375 7.75 3.4375ZM9.5 11.375V10.625C9.5 10.4375 9.3125 10.25 9.125 10.25H8.75V7.125C8.75 6.9375 8.5625 6.75 8.375 6.75H6.375C6.15625 6.75 6 6.9375 6 7.125V7.875C6 8.09375 6.15625 8.25 6.375 8.25H6.75V10.25H6.375C6.15625 10.25 6 10.4375 6 10.625V11.375C6 11.5938 6.15625 11.75 6.375 11.75H9.125C9.3125 11.75 9.5 11.5938 9.5 11.375Z"
                  fill="#304050"
                />
              </svg>

              {showTooltip && (
                <div className="tooltip">
                  <Flex className="tooltip-flex">
                    <Text className="tooltip-title">Password must have:</Text>
                    <Flex flexDirection="row" gap="6px">
                      <CheckboxField checked={password.length >= 10} />
                      <Text className="tooltip-text">
                        at least ten characters
                      </Text>
                    </Flex>
                    <Flex direction="row" gap="6px">
                      <CheckboxField checked={/[a-z]/.test(password)} />
                      <Text className="tooltip-text">one lowercase letter</Text>
                    </Flex>
                    <Flex direction="row" gap="6px">
                      <CheckboxField checked={/[A-Z]/.test(password)} />
                      <Text className="tooltip-text">one uppercase letter</Text>
                    </Flex>
                    <Flex direction="row" gap="6px">
                      <CheckboxField checked={/[0-9]/.test(password)} />
                      <Text className="tooltip-text">one number</Text>
                    </Flex>
                    <Flex direction="row" gap="6px">
                      <CheckboxField
                        checked={/[\^\$\*\.\[\]\{\}\(\)\?\"\!@#%&\/\\,><\':;|_~\`+= -]/.test(
                          password,
                        )}
                      />
                      <Text className="tooltip-text">
                        one special character
                      </Text>
                    </Flex>
                  </Flex>
                </div>
              )}
            </div>
          </Flex>
        ),
        height: "unset",
        value: password,
        onChange: handlePasswordChange,
        hasError: passwordError ? true : false,
        errorMessage: passwordError,
        onFocus: () => {
          setShowTooltip(true);
        },
        onBlur: () => {
          setShowTooltip(false);
        },
      },

      PasswordField38493596: {
        label: "Confirm Password *",
        value: confirmPassword,
        onChange: handleConfirmPasswordChange,
        hasError: confirmPasswordError ? true : false,
        errorMessage: confirmPasswordError,
      },

      CheckboxField: {
        checked: agreeTermsAndPolicy,
        onChange: (event) => {
          if (!event.target.checked) {
            setAgreeTermsAndPolicy(false);
          } else {
            setTosAndPrivatePolicyVisible(true);
          }
        },
        hasError: agreeTermsAndPolicyError ? true : false,
        errorMessage: agreeTermsAndPolicyError,
      },

      Button: {
        onClick: handleSignUp,
      },

      "Already have an account? Login": {
        children: (
          <>
            Already have an account?{" "}
            <span
              onClick={navigateToSignIn}
              style={{
                fontWeight: "600",
                color: "rgba(49,91,155,1)",
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              Login
            </span>
          </>
        ),
      },
    };
  }, [
    showTooltip,
    firstNameError,
    handlePhoneChange,
    isLargeScreen,
    firstName,
    lastNameError,
    lastName,
    emailError,
    email,
    phoneError,
    phone,
    passwordError,
    password,
    confirmPasswordError,
    confirmPassword,
    agreeTermsAndPolicyError,
    agreeTermsAndPolicy,
    handleSignUp,
    navigateToSignIn,
    referralCode,
    referralCodeError,
  ]);

  return (
    <div className="container">
      <div className="side-by-side">
        <GetDiscovered></GetDiscovered>
        <div className="form-container">
          {tosAndPrivatePolicyVisible && (
            <div className="tos-privacy-modal">
              <div className="modal-backdrop"></div>
              <Flex
                width="80%"
                height="70%"
                display="inline-flex"
                padding={isLargeScreen ? "10px 20px 20px 20px" : "20px"}
                direction="column"
                justifyContent="center"
                alignItems="flex-start"
                backgroundColor="rgba(255,255,255,1)"
                borderRadius="4px"
                gap="20px"
              >
                <Flex
                  direction="row"
                  width="100%"
                  //height="5%"
                  justifyContent="space-between"
                >
                  <Text
                    fontSize="22px"
                    fontWeight="600"
                    display="flex"
                    shrink="1"
                  >
                    Terms of Use and Privacy Policy
                  </Text>
                  <Close
                    onClick={(event) => {
                      setTosAndPrivatePolicyVisible(false);
                    }}
                    style={{
                      cursor: "pointer",
                    }}
                  ></Close>
                </Flex>
                <PrivacyPolicyTermsUseCombined
                  width="100%"
                  height="80%"
                  overflow="scroll"
                  overrides={{
                    Button: {
                      variation: "link",
                      children: "",
                      isDisabled: true,
                    },

                    "Privacy Policy": {
                      width: "unset",
                    },

                    "Last Updated on March 16th, 2023560526458": {
                      width: "unset",
                      //shrink: "1"
                    },

                    "Terms of Use": {
                      width: "unset",
                    },

                    "Last Updated on March 16th, 2023560526487": {
                      width: "unset",
                    },
                  }}
                  padding={isLargeScreen ? "59px 79px 59px 79px" : "20px"}
                ></PrivacyPolicyTermsUseCombined>
                <Flex
                  direction="column"
                  width="100%"
                  height="10%"
                  justifyContent="center"
                  alignItems="center"
                  alignSelf="center"
                >
                  <Button
                    width="unset"
                    height="unset"
                    shrink="0"
                    alignSelf="stretch"
                    size="default"
                    isDisabled={false}
                    variation="primary"
                    children="I agree"
                    onClick={(event) => {
                      setAgreeTermsAndPolicy(true);
                      setTosAndPrivatePolicyVisible(false);
                    }}
                  ></Button>
                </Flex>
              </Flex>
            </div>
          )}
          {signUpLoading && (
            <Loader width="50%" height="50%" transform="-50%, -100%" />
          )}

          {signUpStatus === "" && <Signup overrides={signupOverrides}></Signup>}
          {signUpStatus === "Awaiting Verification" && (
            <Verification email={email}></Verification>
          )}

          {signUpStatus === "Verified" && (
            <Success email={email} password={password} />
          )}
        </div>
      </div>
    </div>
  );
}

export default SignUp;
